import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useAuthSession } from "@/features/auth/hooks/use-auth-session";
import type { AuthSession } from "@/features/auth/model/auth-session";
import type { AuthUser } from "@/features/auth/model/auth-user";
import { LogOutIcon } from "@/shared/ui/console-icons";

interface UserProfile {
  primaryLabel: string;
  secondaryLabel: string | null;
  tenantLabel: string | null;
  initials: string;
}

function cleanLabel(value: string | null | undefined): string | null {
  const label = value?.trim();
  return label ? label : null;
}

function getInitials(label: string, email: string | null): string {
  const nameParts = label
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .filter(Boolean);

  if (nameParts.length >= 2) {
    return `${nameParts[0]}${nameParts[1]}`.toUpperCase();
  }

  if (nameParts.length === 1 && label !== email) {
    return nameParts[0].toUpperCase();
  }

  const emailInitial = email?.charAt(0);
  return emailInitial ? emailInitial.toUpperCase() : "?";
}

function getTenantLabel(authSession: AuthSession | undefined): string | null {
  const tenantSlug = cleanLabel(authSession?.tenant.slug);
  const tenantId = cleanLabel(authSession?.tenant.id);

  if (tenantSlug && tenantId) {
    return `${tenantSlug} (${tenantId})`;
  }

  return tenantSlug ?? tenantId;
}

function getUserProfile(user: AuthUser, authSession: AuthSession | undefined): UserProfile {
  const email = cleanLabel(authSession?.user.email) ?? cleanLabel(user.email);
  const displayName = cleanLabel(authSession?.user.display_name) ?? cleanLabel(user.displayName);
  const primaryLabel = displayName ?? email ?? "Signed-in user";
  const secondaryLabel = email && email !== primaryLabel ? email : null;

  return {
    primaryLabel,
    secondaryLabel,
    tenantLabel: getTenantLabel(authSession),
    initials: getInitials(primaryLabel, email),
  };
}

// ----- Private internal components -----

interface UserAvatarProps {
  initials: string;
}

function UserAvatar({ initials }: UserAvatarProps) {
  return (
    <Avatar
      sx={(theme) => ({
        width: 30,
        height: 30,
        borderRadius: "6px",
        bgcolor: theme.palette.primary.main,
        fontSize: "0.6875rem",
        fontWeight: 700,
        flexShrink: 0,
      })}
    >
      {initials}
    </Avatar>
  );
}

interface TriggerProps {
  open: boolean;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
  profile: UserProfile;
}

function CollapsedTrigger({ open, onOpen, profile }: TriggerProps) {
  return (
    <Tooltip title={profile.primaryLabel} placement="right" arrow>
      <ButtonBase
        onClick={onOpen}
        aria-label={`User menu: ${profile.primaryLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{ borderRadius: "6px" }}
      >
        <UserAvatar initials={profile.initials} />
      </ButtonBase>
    </Tooltip>
  );
}

interface ExpandedTriggerProps extends TriggerProps {
  onSignOut: () => void;
  isSigningOut: boolean;
}

function ExpandedTrigger({ open, onOpen, onSignOut, isSigningOut, profile }: ExpandedTriggerProps) {
  return (
    <>
      <ButtonBase
        onClick={onOpen}
        aria-label={`User menu: ${profile.primaryLabel}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={(theme) => ({
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          px: 1,
          py: 0.5,
          borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
          "&:hover": {
            backgroundColor: alpha(theme.palette.common.white, 0.05),
          },
        })}
      >
        <UserAvatar initials={profile.initials} />
        <Box sx={{ minWidth: 0, textAlign: "left" }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "text.primary",
            }}
          >
            {profile.primaryLabel}
          </Typography>
          {profile.secondaryLabel && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {profile.secondaryLabel}
            </Typography>
          )}
        </Box>
      </ButtonBase>

      <Tooltip title="Sign out" arrow>
        <IconButton
          aria-label="Sign out"
          disabled={isSigningOut}
          onClick={onSignOut}
          sx={(theme) => ({
            color: "text.secondary",
            mr: 0.5,
            alignSelf: "stretch",
            height: "auto",
            borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
            "&:hover": { color: "text.primary" },
          })}
        >
          <LogOutIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Tooltip>
    </>
  );
}

interface SessionPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSignOut: () => void;
  isSigningOut: boolean;
  signOutFailed: boolean;
  profile: UserProfile;
}

function SessionPopover({
  open,
  anchorEl,
  onClose,
  onSignOut,
  isSigningOut,
  signOutFailed,
  profile,
}: SessionPopoverProps) {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            width: 220,
            mb: 0.5,
            p: 2,
            border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          }),
        },
      }}
    >
      <Typography variant="overline" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
        Signed in
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: "text.primary", overflowWrap: "anywhere" }}
      >
        {profile.primaryLabel}
      </Typography>
      {profile.secondaryLabel && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", overflowWrap: "anywhere" }}
        >
          {profile.secondaryLabel}
        </Typography>
      )}
      {profile.tenantLabel && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", mt: 0.25, overflowWrap: "anywhere" }}
        >
          {profile.tenantLabel}
        </Typography>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
        <Box
          aria-hidden="true"
          sx={(theme) => ({
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: theme.palette.success.main,
            flexShrink: 0,
          })}
        />
        <Typography
          variant="body2"
          sx={{ fontFamily: '"Roboto Mono", monospace', fontSize: "0.75rem" }}
        >
          Session Active
        </Typography>
      </Box>
      {signOutFailed && (
        <Typography variant="caption" sx={(theme) => ({ color: theme.palette.error.main, mb: 1 })}>
          Sign out failed. Please try again.
        </Typography>
      )}
      <ButtonBase
        onClick={onSignOut}
        disabled={isSigningOut}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: "100%",
          px: 1.5,
          py: 0.875,
          borderRadius: `${(theme.shape.borderRadius as number) / 2}px`,
          border: `1px solid ${alpha(theme.palette.error.main, 0.4)}`,
          color: theme.palette.error.main,
          justifyContent: "flex-start",
          "&:hover": {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
          },
        })}
      >
        <LogOutIcon sx={{ fontSize: "1rem" }} />
        <Typography variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
          {isSigningOut ? "Signing out..." : "Sign out"}
        </Typography>
      </ButtonBase>
    </Popover>
  );
}

// ----- Public component -----

interface UserSessionMenuProps {
  collapsed: boolean;
}

export function UserSessionMenu({ collapsed }: UserSessionMenuProps) {
  const { user, signOut } = useAuth();
  const authSession = useAuthSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);
  const open = Boolean(anchorEl);

  if (!user) {
    return null;
  }

  const profile = getUserProfile(user, authSession.data);

  function handleOpen(event: MouseEvent<HTMLElement>) {
    setSignOutFailed(false);
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }

  async function handleSignOut() {
    if (isSigningOut) {
      return;
    }

    setSignOutFailed(false);
    setIsSigningOut(true);

    try {
      await signOut();
      queryClient.clear();
      navigate("/login", { replace: true });
    } catch {
      setSignOutFailed(true);
      setIsSigningOut(false);
    }
  }

  return (
    <>
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "stretch",
          px: 0.5,
          py: 0.75,
          gap: 0,
          justifyContent: collapsed ? "center" : "flex-start",
          borderTop: `1px solid ${alpha(theme.palette.common.white, 0.07)}`,
        })}
      >
        {collapsed ? (
          <CollapsedTrigger open={open} onOpen={handleOpen} profile={profile} />
        ) : (
          <ExpandedTrigger
            open={open}
            onOpen={handleOpen}
            onSignOut={() => {
              void handleSignOut();
            }}
            isSigningOut={isSigningOut}
            profile={profile}
          />
        )}
      </Box>
      <SessionPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onSignOut={() => {
          void handleSignOut();
        }}
        isSigningOut={isSigningOut}
        signOutFailed={signOutFailed}
        profile={profile}
      />
    </>
  );
}
